import React, { useState, useRef } from "react";
import type { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";

interface UploadRevisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaItem: MediaItem;
  clientId: string;
  onUploadComplete: (updatedItem: MediaItem) => void;
  onError: (error: string) => void;
}

export const UploadRevisionModal: React.FC<UploadRevisionModalProps> = ({
  isOpen,
  onClose,
  mediaItem,
  clientId,
  onUploadComplete,
  onError,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptTypes = mediaItem.isVideo() ? "video/*" : "image/*";
  const mediaTypeLabel = mediaItem.isVideo() ? "video" : "image";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const { imageActionsService } = await import("@/core/MediaItem/ImageActionsService");
      const result = await imageActionsService.uploadRevisionComplete(
        clientId,
        mediaItem.id,
        mediaItem.fileIdentity,
        selectedFile
      );

      if (result.success && result.mediaItem) {
        const updatedMediaItem = mediaItem.clone(result.mediaItem);
        onUploadComplete(updatedMediaItem);
        onClose();
      } else {
        throw new Error(result.error || "Failed to upload revision");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to upload revision";
      onError(errorMessage);
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Revision"
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            isLoading={isUploading}
          >
            Upload
          </Button>
        </>
      }
    >
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-600">
          Select a new {mediaTypeLabel} file to replace the current one. The file will be reprocessed after upload.
        </p>

        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes}
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-lg file:border-0
              file:text-sm file:font-medium
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100
              cursor-pointer"
            disabled={isUploading}
          />
        </div>

        {selectedFile && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700 truncate">
              {selectedFile.name}
            </p>
            <p className="text-xs text-gray-500">
              {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};
